import { useEffect, useState } from "react";
import { API } from "../../lib/api";
import { Decor, Marquee, Pic, Btn, RADIUS, ART_BY_HERO, LiveCtx, LABEL, EditCtx, ET, SiteCtx, pageBg } from "./SiteParts";
import { SiteSection } from "./SiteSections";
import { EditZone } from "./EditZone";

function useFonts(fonts) {
  useEffect(() => {
    if (!fonts) return;
    const fam = [fonts.heading, fonts.body].filter(Boolean).map((f) => `family=${f.replace(/ /g, "+")}:wght@400;600;700`).join("&");
    const id = `pv-font-${fam}`;
    if (document.getElementById(id)) return;
    const l = document.createElement("link"); l.id = id; l.rel = "stylesheet"; l.href = `https://fonts.googleapis.com/css2?${fam}&display=swap`;
    document.head.appendChild(l);
  }, [fonts]);
}

function HeroText({ c, t, pal, size, light, center, href, more }) {
  return (
    <div className={`relative z-10 ${center ? "text-center mx-auto max-w-3xl" : ""}`}>
      {c.tagline && <ET as="p" path={["tagline"]} value={c.tagline} className="text-[11px] uppercase tracking-[.3em] font-semibold" style={{ color: light ? "#ffffffd9" : pal.accent }} />}
      <ET as="h1" path={["hero", "title"]} value={c.hero.title} style={{ fontFamily: t.fonts.heading, color: light ? "#fff" : pal.primary, letterSpacing: "-0.02em" }} className={`block mt-4 leading-[1.02] ${size}`} />
      <ET as="p" path={["hero", "subtitle"]} value={c.hero.subtitle} className={`mt-5 text-sm md:text-base leading-relaxed max-w-xl ${center ? "mx-auto" : ""}`} style={{ opacity: 0.8 }} />
      <div className={`mt-7 flex flex-wrap gap-3 items-center ${center ? "justify-center" : ""}`}>
        <Btn t={t} invert={light} href={href}>{c.hero.cta}</Btn>
        <a href={more} className="text-xs font-semibold underline underline-offset-4" style={{ color: light ? "#fff" : pal.primary }}>Learn more</a>
      </div>
    </div>);
}

function Hero({ c, t, l, mobile, r, href, sections, more, live }) {
  const pal = t.palette, art = l.art || ART_BY_HERO[l.hero] || "geometric";
  const big = mobile ? "text-4xl" : "text-6xl";
  const pad = mobile ? "px-[6%] py-12" : "px-[6%] py-20";
  const tx = { c, t, pal, href, more };
  const D = (p) => <Decor art={art} pal={pal} mobile={mobile} {...p} />;
  if (l.hero === "fullbleed") {
    return (
      <section className={`relative overflow-hidden ${pad}`} style={{ background: pal.primary, color: "#fff" }} data-testid="preview-hero">
        {c.hero.image_asset_id && <img src={`${API}/assets/${c.hero.image_asset_id}`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <D dark />
        <span aria-hidden className="absolute -right-6 -bottom-16 leading-none select-none" style={{ fontFamily: t.fonts.heading, fontSize: mobile ? 220 : 420, color: "transparent", WebkitTextStroke: `2px ${pal.accent}55` }}>{(c.site_name || "A")[0]}</span>
        <div className={mobile ? "" : "max-w-[62%] py-8"}><HeroText {...tx} size={mobile ? "text-4xl" : "text-7xl"} light /></div>
      </section>);
  }
  if (l.hero === "centered") {
    return (
      <section className={`relative overflow-hidden ${pad}`} data-testid="preview-hero">
        <D />
        <HeroText {...tx} size={big} center />
        <div className="relative z-10 mt-8 flex flex-wrap justify-center gap-2">{sections.slice(0, 4).map((s) => <span key={s.id} className="text-[11px] px-3 py-1 rounded-full border" style={{ borderColor: `${pal.primary}33`, background: `${pal.surface}cc` }}>{s.title}</span>)}</div>
        {c.hero.image_asset_id && <div className="relative z-10 mt-10 max-w-4xl mx-auto shadow-2xl" style={{ borderRadius: r }}><Pic id={c.hero.image_asset_id} alt={`${c.site_name} hero`} r={r} label="Your hero image" /></div>}
      </section>);
  }
  const noPic = !c.hero.image_asset_id;
  if (l.hero === "stacked" && !noPic) {
    return (
      <section className={`relative overflow-hidden ${pad}`} data-testid="preview-hero">
        <D />
        <HeroText {...tx} size={mobile ? "text-4xl" : "text-7xl"} />
        <div className="relative z-10 mt-10">
          <Pic id={c.hero.image_asset_id} alt={`${c.site_name} hero`} r={r} label="Your hero image" />
          {!mobile && <div className="absolute -bottom-6 right-6 grid grid-cols-3 gap-3 w-[55%]">{sections.slice(0, 3).map((s, i) => (
            <div key={s.id} className="p-4 shadow-xl" style={{ borderRadius: r, background: [pal.primary, pal.accent, pal.surface][i], color: i === 2 ? pal.text : "#fff" }}>
              <p className="text-[10px] uppercase tracking-widest opacity-80">0{i + 1}</p><p className="text-sm font-semibold mt-1 line-clamp-2">{s.title}</p></div>))}</div>}
        </div>
      </section>);
  }
  const asym = l.hero === "asymmetric";
  if (noPic && l.hero !== "centered" && l.hero !== "fullbleed") {
    return (
      <section className={`relative overflow-hidden ${pad}`} data-testid="preview-hero">
        <D />
        <div className={mobile ? "" : "max-w-[70%]"}><HeroText {...tx} size={mobile ? "text-4xl" : "text-7xl"} /></div>
        {!mobile && <div className="relative z-10 mt-12 grid grid-cols-3 gap-3 max-w-[70%]">{sections.slice(0, 3).map((s, i) => (
          <a key={s.id} href={`#sec-${s.id}`} className="pv-card p-4" style={{ borderRadius: r, background: [pal.primary, pal.accent, pal.surface][i], color: i === 2 ? pal.text : "#fff" }}>
            <p className="text-[10px] uppercase tracking-widest opacity-80">0{i + 1}</p><p className="text-sm font-semibold mt-1 line-clamp-2">{s.title}</p></a>))}</div>}
      </section>);
  }
  return (
    <section className={`relative overflow-hidden ${pad} ${mobile ? "" : `grid ${asym ? "grid-cols-12" : "grid-cols-2"} gap-12 items-center`}`} data-testid="preview-hero">
      <D />
      <div className={asym && !mobile ? "col-span-7" : ""}><HeroText {...tx} size={asym && !mobile ? "text-7xl" : big} /></div>
      <div className={`relative z-10 ${mobile ? "mt-10" : asym ? "col-span-5" : ""}`}>
        <div className="absolute -inset-3 translate-x-5 translate-y-5" style={{ borderRadius: asym ? "48% 52% 40% 60% / 55% 40% 60% 45%" : r, background: asym ? pal.accent : "transparent", border: asym ? "none" : `2px solid ${pal.accent}`, opacity: asym ? 0.35 : 1 }} />
        <div className="relative"><Pic id={c.hero.image_asset_id} alt={`${c.site_name} hero`} r={asym ? "40% 40% 12px 12px" : r} tall={asym} label="Your hero image" /></div>
        {!mobile && <div className="pv-float absolute -left-8 bottom-8 px-4 py-3 shadow-xl text-xs" style={{ background: pal.surface, borderRadius: r, color: pal.text }}>
          <p className="font-semibold" style={{ color: pal.primary }}>{sections[0]?.title || c.site_name}</p><p className="opacity-70">{c.tagline || "Crafted for you"}</p></div>}
      </div>
    </section>);
}

export default function SitePreview({ spec, device = "desktop", live = false, edit = null, site = null, onNav = null, navSections = null }) {
  const t = spec.theme, l = spec.layout, c = spec.content;
  useFonts(t.fonts);
  const [menu, setMenu] = useState(false);
  const mobile = device === "mobile";
  const r = RADIUS[t.radius] || "8px";
  const pal = t.palette;
  const order = l.sections_order?.length ? l.sections_order : c.sections.map((s) => s.id);
  const sections = [...order, ...c.sections.map((s) => s.id).filter((id) => !order.includes(id))].map((id) => c.sections.find((s) => s.id === id)).filter(Boolean);
  const navList = navSections || sections;
  const contact = sections.find((s) => s.type === "contact") || sections.find((s) => s.type === "booking");
  const href = contact ? `#sec-${contact.id}` : undefined;
  const navDark = t.nav_style === "dark";
  const navLabel = (s) => (s.title.length > 18 ? LABEL[s.type] || s.title.slice(0, 18) : s.title);
  const more = sections[0] ? `#sec-${sections[0].id}` : undefined;
  // Preview: internal links scroll inside the preview (never leave it); editing keeps clicks for editing.
  const inPreviewNav = (e) => {
    if (live) return;
    const a = e.target.closest("a[href^='#']");
    if (!a) return;
    e.preventDefault();
    if (edit) return;
    const id = a.getAttribute("href").slice(1);
    const el = id && e.currentTarget.querySelector(`[id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); else onNav?.(id);
  };
  const logo = c.logo_asset_id ? <img src={`${API}/assets/${c.logo_asset_id}`} alt={`${c.site_name} logo`} className="h-8 w-auto" /> : <span style={{ fontFamily: t.fonts.heading }} className={`text-lg font-semibold ${mobile ? "truncate min-w-0" : "shrink-0 whitespace-nowrap"}`}>{c.site_name}</span>;
  return (
    <LiveCtx.Provider value={live}>
    <SiteCtx.Provider value={site}>
    <EditCtx.Provider value={edit}>
    <div style={{ ...pageBg(t.background_style, pal, c.site_name, t.background_asset_id), color: pal.text, fontFamily: t.fonts.body, overflowX: "clip" }} className="min-h-full" data-testid="site-preview" onClickCapture={inPreviewNav}>
      <EditZone target={{ kind: "hero" }} label="logo, name & headline">
      <header style={{ background: navDark ? pal.primary : `${pal.surface}ee`, color: navDark ? "#fff" : pal.text, backdropFilter: "blur(12px)" }} className={`${live ? "sticky top-0 z-30" : ""} px-[6%] py-4 flex items-center gap-4 border-b ${mobile ? "flex-wrap justify-between" : l.nav === "centered" ? "flex-col gap-2" : "justify-between"}`}>
        {mobile ? <div className="min-w-0 flex-1 truncate">{logo}</div> : logo}
        {mobile ? <button type="button" onClick={() => setMenu((x) => !x)} className="text-xs border px-2 py-1 rounded" aria-label="Menu" aria-expanded={menu} data-testid="site-menu-btn">{menu ? "Close" : "Menu"}</button> : (
          <nav className={`flex flex-wrap justify-end items-center gap-x-6 gap-y-1 text-sm ${device === "tablet" ? "gap-x-4 text-xs" : ""}`}>{navList.slice(0, device === "tablet" ? 3 : 5).map((s) => <a key={s.id} href={`#sec-${s.id}`} className="opacity-80 hover:opacity-100">{navLabel(s)}</a>)}
            <Btn t={t} small href={href} invert={navDark}>{c.hero.cta}</Btn></nav>)}
        {mobile && menu && <nav className="basis-full flex flex-col gap-3 pt-3 text-sm" data-testid="site-mobile-menu">{navList.map((s) => <a key={s.id} href={`#sec-${s.id}`} onClick={() => setMenu(false)}>{navLabel(s)}</a>)}</nav>}
      </header>
      <Hero c={c} t={t} l={l} mobile={mobile} r={r} href={href} sections={sections} more={more} live={live} />
      </EditZone>
      <Marquee words={[c.tagline, ...sections.map((s) => s.title)].filter(Boolean)} pal={pal} font={t.fonts.heading} />
      {sections.map((s, i) => <SiteSection key={s.id} s={s} i={i} t={t} l={l} mobile={mobile} r={r} href={href} />)}
      <EditZone target={{ kind: "footer" }} label="footer">
      <footer style={{ background: pal.primary, color: "#ffffffcc" }} className="px-[6%] pt-12 pb-6 text-xs">
        <div className={`grid gap-8 ${mobile ? "grid-cols-1" : "grid-cols-3"}`}>
          <div><p className="text-xl text-white" style={{ fontFamily: t.fonts.heading }}>{c.site_name}</p><p className="mt-2 opacity-80">{c.tagline}</p></div>
          <div className="flex flex-col gap-1.5">{navList.slice(0, 6).map((s) => <a key={s.id} href={`#sec-${s.id}`} className="hover:text-white">{s.title}</a>)}</div>
          <p className="whitespace-pre-line">{c.footer}</p>
        </div>
        <p className="mt-10 pt-4 border-t border-white/15 opacity-70">© {new Date().getFullYear()} {c.site_name}</p>
      </footer>
      </EditZone>
    </div>
    </EditCtx.Provider>
    </SiteCtx.Provider>
    </LiveCtx.Provider>
  );
}
